import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SendErrorMessageService, ErrorData } from '@awo-care/shared-core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EmployeeErrorHandlerService } from '../../services/employee-error-handler.service';

@Component({
  selector: 'app-send-error-message-employee',
  template: `
    <div class="error-container">
      @if (errorData) {
      <form [formGroup]="errorForm" (ngSubmit)="sendError()" class="error-form">
        <h1>{{ errorData.header }}</h1>
        <div>{{ errorData.message }}</div>

        <mat-form-field appearance="outline">
          <mat-label>Kommentar</mat-label>
          <textarea
            matInput
            placeholder="Bei welcher Aktion ist der Fehler aufgetreten?"
            formControlName="comment"
            required
          ></textarea>
          <mat-error>Bitte geben Sie einen Kommentar ein</mat-error>
        </mat-form-field>

        <div class="button-area">
          <button mat-button type="button" (click)="cancel()">Abbrechen</button>
          <button mat-flat-button type="submit" [disabled]="!errorForm.valid || isSubmitting()">
            @if (!isSubmitting()) { Fehlerbericht senden } @else {
            <mat-spinner diameter="20"></mat-spinner>
            }
          </button>
        </div>
      </form>
      } @else {
      <div class="no-data">
        <h2>Keine Fehlerdaten vorhanden</h2>
        <button mat-stroked-button (click)="navigateBack()">Zurück</button>
      </div>
      }
    </div>
  `,
  styles: [
    `
      .error-container {
        margin: 0 auto;
        padding: 16px;
        max-width: 800px;
        min-width: 300px;
      }

      .error-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 100%;

        h1 {
          margin: 0;
          text-align: center;
        }

        mat-form-field {
          width: 100%;
        }

        textarea {
          min-height: 100px;
        }
      }

      .button-area {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 32px;

        button {
          height: 48px;
        }
      }

      .no-data {
        text-align: center;
        padding: 32px;

        h2 {
          margin-bottom: 16px;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
})
export class SendErrorMessageemployeeComponent {
  private router = inject(Router);
  private location = inject(Location);
  private sendErrorMessageService = inject(SendErrorMessageService);
  private snackBar = inject(MatSnackBar);
  private employeeErrorHandlerService = inject(EmployeeErrorHandlerService);

  isSubmitting = signal(false);

  protected errorData = this.router.currentNavigation()?.extras?.state?.[
    'data'
  ] as GlobalError;
  protected errorForm = new FormGroup({
    comment: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });

  async sendError(): Promise<void> {
    if (this.errorForm.valid && this.errorData) {
      this.isSubmitting.set(true);
      try {
        const uid = this.employeeErrorHandlerService.getUserId();
        const userEmail = this.employeeErrorHandlerService.getUserEmail();
        const tierName = this.employeeErrorHandlerService.getEnvironment();
        const appType = this.employeeErrorHandlerService.getAppType();
        const appVersion = this.employeeErrorHandlerService.getAppVersion();

        const errorDataToSend: ErrorData = {
          errorData: this.errorData,
          comment: this.errorForm.get('comment')?.value || '',
          uid,
          userEmail,
          tierName,
          appType,
          appVersion,
        };

        await this.sendErrorMessageService.sendError(errorDataToSend);
        this.snackBar.open('Fehler erfolgreich gemeldet', 'OK', {
          duration: 3000,
        });
        this.navigateBack();
      } catch (error) {
        console.error('Failed to send error report:', error);
      } finally {
        this.isSubmitting.set(false);
      }
    }
  }

  cancel(): void {
    this.navigateBack();
  }

  navigateBack(): void {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      void this.router.navigate(['/']);
    }
  }
}
